/**
 * Consent Manager - Multi-jurisdictional privacy compliance
 * Handles cookie consent for GDPR (EU), CCPA/CPRA (US), PDPA (Asia)
 */

import type {
  ConsentPreferences,
  ConsentRecord,
  CookieCategory
} from '../types/consent';
import {
  CONSENT_VERSION,
  CONSENT_EXPIRY_MONTHS,
  CONSENT_STORAGE_KEY
} from '../types/consent';

/**
 * Simple hash function for IP addresses (for audit trail)
 * Not cryptographically secure, just for basic anonymization
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Get default consent preferences (essential only)
 */
const getDefaultPreferences = (): ConsentPreferences => ({
  essential: true,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version: CONSENT_VERSION
});

/**
 * Calculate expiry date (12 months from now)
 */
const calculateExpiryDate = (): number => {
  const now = new Date();
  now.setMonth(now.getMonth() + CONSENT_EXPIRY_MONTHS);
  return now.getTime();
};

/**
 * Check if consent has expired
 */
const isConsentExpired = (expiresAt: number): boolean => {
  return Date.now() > expiresAt;
};

/**
 * Check if consent version has changed
 */
const isConsentVersionOutdated = (version: string): boolean => {
  return version !== CONSENT_VERSION;
};

/**
 * Get stored consent record from localStorage
 */
export const getConsentRecord = (): ConsentRecord | null => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const record: ConsentRecord = JSON.parse(stored);

    // Check if consent has expired or version is outdated
    if (isConsentExpired(record.expiresAt) || isConsentVersionOutdated(record.preferences.version)) {
      console.log('Consent expired or outdated, removing stored consent');
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return record;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return null;
  }
};

/**
 * Get current consent preferences
 * Returns default (essential only) if no consent given
 */
export const getConsent = (): ConsentPreferences => {
  const record = getConsentRecord();
  return record?.preferences || getDefaultPreferences();
};

/**
 * Check if user has consented to a specific category
 */
export const hasConsent = (category: CookieCategory): boolean => {
  const preferences = getConsent();
  return preferences[category] === true;
};

/**
 * Check if user has made any consent choice
 * Used to determine if banner should be shown
 */
export const hasConsentChoice = (): boolean => {
  return getConsentRecord() !== null;
};

/**
 * Save consent preferences with audit trail
 */
export const setConsent = (preferences: Partial<ConsentPreferences>, ipAddress?: string): void => {
  try {
    const current = getConsent();

    // Merge with current preferences
    const newPreferences: ConsentPreferences = {
      ...current,
      ...preferences,
      essential: true, // Always true
      timestamp: Date.now(),
      version: CONSENT_VERSION
    };

    // Add optional IP hash for audit
    if (ipAddress) {
      newPreferences.ipHash = simpleHash(ipAddress);
    }

    const record: ConsentRecord = {
      preferences: newPreferences,
      userAgent: navigator.userAgent,
      expiresAt: calculateExpiryDate(),
      lastUpdated: Date.now()
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));

    // Dispatch custom event so components can react to consent changes
    window.dispatchEvent(new CustomEvent('consentChanged', {
      detail: newPreferences
    }));

    console.log('Consent preferences saved:', newPreferences);
  } catch (error) {
    console.error('Error saving consent preferences:', error);
  }
};

/**
 * Update specific consent categories
 */
export const updateConsent = (updates: Partial<Pick<ConsentPreferences, 'analytics' | 'marketing'>>): void => {
  const current = getConsent();
  setConsent({
    ...current,
    ...updates
  });
};

/**
 * Accept all non-essential cookies
 */
export const acceptAllConsent = (): void => {
  setConsent({
    analytics: true,
    marketing: true
  });
};

/**
 * Reject all non-essential cookies (essential only)
 */
export const rejectAllConsent = (): void => {
  setConsent({
    analytics: false,
    marketing: false
  });
};

/**
 * Revoke all consent and clear preferences
 * Used for GDPR "right to withdraw consent"
 */
export const revokeConsent = (): void => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);

    // Set to essential only
    setConsent({
      analytics: false,
      marketing: false
    });

    console.log('All consent revoked');
  } catch (error) {
    console.error('Error revoking consent:', error);
  }
};

/**
 * Get consent timestamp (when user last made a choice)
 */
export const getConsentTimestamp = (): Date | null => {
  const record = getConsentRecord();
  return record ? new Date(record.preferences.timestamp) : null;
};

/**
 * Get consent expiry date
 */
export const getConsentExpiryDate = (): Date | null => {
  const record = getConsentRecord();
  return record ? new Date(record.expiresAt) : null;
};

/**
 * Check if consent needs renewal (within 30 days of expiry)
 */
export const needsConsentRenewal = (): boolean => {
  const record = getConsentRecord();
  if (!record) return false;

  const thirtyDaysBeforeExpiry = record.expiresAt - (30 * 24 * 60 * 60 * 1000);
  return Date.now() > thirtyDaysBeforeExpiry;
};

/**
 * Export consent record for user download (GDPR data portability)
 */
export const exportConsentData = (): string => {
  const record = getConsentRecord();
  if (!record) return JSON.stringify({ message: 'No consent data found' }, null, 2);

  return JSON.stringify({
    preferences: record.preferences,
    userAgent: record.userAgent,
    consentGiven: new Date(record.preferences.timestamp).toISOString(),
    expiresAt: new Date(record.expiresAt).toISOString(),
    lastUpdated: new Date(record.lastUpdated).toISOString(),
    version: record.preferences.version
  }, null, 2);
};

/**
 * Clear all tracking cookies based on category
 * Used when revoking consent
 */
export const clearTrackingCookies = (category: CookieCategory): void => {
  const cookiesToClear: Record<CookieCategory, string[]> = {
    essential: [], // Never clear essential cookies
    analytics: ['_ga', '_gid', '_gat', '_clck', '_clsk'],
    marketing: ['_fbp', '_fbc', 'hubspotutk']
  };

  const cookies = cookiesToClear[category];
  cookies.forEach(cookieName => {
    // Set cookie to expire in the past
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Also try with domain
    const domain = window.location.hostname;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
  });
};
